import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockVerticals } from '@/mocks/verticals';
import { mockLeads } from '@/mocks/leads';
import { mockOffers } from '@/mocks/offers';
import { mockPartners } from '@/mocks/partners';
import { mockProperties } from '@/mocks/properties';
import { mockFunnels } from '@/mocks/funnels';
import { mockGeos } from '@/mocks/geos';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Layers,
  MapPin,
  Users,
  FileText,
  Activity,
  BarChart3,
  Globe,
  PhoneCall,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Link2,
  Edit3,
  Save,
  X,
} from 'lucide-react';

export default function VerticalDetailPage() {
  const { verticalId } = useParams<{ verticalId: string }>();
  const navigate = useNavigate();
  const vertical = useMemo(() => mockVerticals.find(v => v.id === verticalId), [verticalId]);

  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['overview', 'intake-schema']));
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ duplicate_window_days: 0, requires_tcpa_consent: false });

  const verticalLeads = useMemo(() => mockLeads.filter(l => l.vertical_id === vertical?.id && !l.deleted_at), [vertical?.id]);
  const monthLeads = useMemo(() => {
    const now = new Date();
    return verticalLeads.filter(l => {
      const d = new Date(l.created_at);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
  }, [verticalLeads]);

  const properties = useMemo(() => mockProperties.filter(p => p.vertical_id === vertical?.id), [vertical?.id]);
  const offers = useMemo(() => mockOffers.filter(o => o.vertical_id === vertical?.id), [vertical?.id]);
  const activeOffers = useMemo(() => offers.filter(o => o.status === 'active'), [offers]);
  const partnerIds = useMemo(() => new Set(offers.map(o => o.partner_id)), [offers]);
  const partners = useMemo(() => mockPartners.filter(p => partnerIds.has(p.id)), [partnerIds]);
  const funnels = useMemo(() => mockFunnels.filter(f => properties.some(p => p.id === f.property_id)), [properties]);

  const funnelByStatus = useMemo(() => {
    const map: Record<string, number> = {};
    monthLeads.forEach(l => {
      map[l.status] = (map[l.status] || 0) + 1;
    });
    return map;
  }, [monthLeads]);

  const routingByStatus = useMemo(() => {
    const map: Record<string, number> = { assigned: 0, unmatched: 0, duplicate: 0, invalid: 0, pending: 0 };
    monthLeads.forEach(l => { map[l.routing_status] = (map[l.routing_status] || 0) + 1; });
    return map;
  }, [monthLeads]);

  const topGeos = useMemo(() => {
    const geoCounts: Record<string, number> = {};
    verticalLeads.forEach(l => { geoCounts[l.geo_id] = (geoCounts[l.geo_id] || 0) + 1; });
    return Object.entries(geoCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([geoId, count]) => {
        const geo = mockGeos.find(g => g.id === geoId);
        return { id: geoId, name: geo?.name || geoId, count };
      });
  }, [verticalLeads]);

  const avgQuality = useMemo(() => {
    const scores = monthLeads.map(l => l.quality_score).filter(Boolean) as number[];
    return scores.length > 0 ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100) : 0;
  }, [monthLeads]);

  if (!vertical) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'var(--bg-input)' }}>
          <Layers size={20} style={{ color: 'var(--text-muted)' }} />
        </div>
        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Vertical not found</p>
        <button
          onClick={() => navigate('/verticals')}
          className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border transition-colors"
          style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}
        >
          <ArrowLeft size={14} /> Back to Verticals
        </button>
      </div>
    );
  }

  const toggleSection = (key: string) => {
    const next = new Set(expandedSections);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExpandedSections(next);
  };

  const startEdit = () => {
    setEditForm({
      duplicate_window_days: vertical.duplicate_window_days,
      requires_tcpa_consent: vertical.requires_tcpa_consent,
    });
    setEditing(true);
  };

  const saveEdit = () => {
    // In real app this would hit the API
    setEditing(false);
  };

  const SectionHeader = ({ label, icon, sectionKey, count }: { label: string; icon: React.ReactNode; sectionKey: string; count?: number }) => (
    <button
      onClick={() => toggleSection(sectionKey)}
      className="w-full flex items-center justify-between py-3 group"
    >
      <div className="flex items-center gap-2.5">
        <div style={{ color: 'var(--text-muted)' }}>{icon}</div>
        <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{label}</span>
        {count !== undefined && (
          <span className="text-[11px] px-1.5 py-0.5 rounded-md" style={{ background: 'var(--bg-input)', color: 'var(--text-muted)' }}>
            {count}
          </span>
        )}
      </div>
      {expandedSections.has(sectionKey) ? (
        <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
      ) : (
        <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
      )}
    </button>
  );

  return (
    <div className="space-y-5 max-w-5xl">
      {/* Breadcrumb */}
      <button
        onClick={() => navigate('/verticals')}
        className="flex items-center gap-1.5 text-[12px] transition-colors hover-muted"
      >
        <ArrowLeft size={13} /> All Verticals
      </button>

      {/* Hero header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: vertical.active ? 'var(--accent-dim)' : 'var(--bg-input)' }}
          >
            <Layers size={22} style={{ color: vertical.active ? 'var(--accent)' : 'var(--text-muted)' }} />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>{vertical.name}</h1>
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border"
                style={vertical.active
                  ? { background: 'var(--accent-dim)', color: 'var(--accent)', borderColor: 'var(--accent-glow)' }
                  : { background: 'var(--bg-input)', color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }
                }
              >
                {vertical.active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
              {verticalLeads.length} total leads · {monthLeads.length} this month · Avg quality {avgQuality}%
            </p>
          </div>
        </div>
        <button
          onClick={() => editing ? saveEdit() : startEdit()}
          className="flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium rounded-lg border transition-all hover-surface"
          style={{ borderColor: 'var(--border-primary)', color: 'var(--text-tertiary)' }}
        >
          {editing ? <><Save size={13} /> Save</> : <><Edit3 size={13} /> Edit</>}
        </button>
      </div>

      {/* Quick stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Properties', value: properties.length, icon: <MapPin size={14} />, color: 'var(--text-primary)' },
          { label: 'Active Offers', value: activeOffers.length, icon: <FileText size={14} />, color: 'var(--accent)' },
          { label: 'Partners', value: partners.length, icon: <Users size={14} />, color: 'var(--text-primary)' },
          { label: 'Funnels', value: funnels.length, icon: <Activity size={14} />, color: 'var(--text-primary)' },
        ].map(s => (
          <div key={s.label} className="rounded-xl border p-4" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center gap-1.5 mb-1" style={{ color: 'var(--text-muted)' }}>
              {s.icon}
              <span className="text-[11px] font-medium">{s.label}</span>
            </div>
            <p className="text-lg font-semibold" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Overview section */}
      <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
        <div className="px-5" style={{ borderBottom: expandedSections.has('overview') ? '1px solid var(--border-separator)' : 'none' }}>
          <SectionHeader label="Pipeline Overview" icon={<BarChart3 size={15} />} sectionKey="overview" />
        </div>
        {expandedSections.has('overview') && (
          <div className="px-5 py-4 space-y-5">
            {/* Funnel status bars */}
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>Lead Status Distribution (this month)</p>
              <div className="space-y-2">
                {Object.entries(funnelByStatus).sort((a, b) => b[1] - a[1]).map(([status, count]) => {
                  const pct = monthLeads.length > 0 ? (count / monthLeads.length) * 100 : 0;
                  const colorMap: Record<string, string> = {
                    new: 'rgba(100,116,139,0.40)',
                    contacted: 'rgba(59,130,246,0.40)',
                    consultation_scheduled: 'rgba(168,85,247,0.40)',
                    retained: 'var(--success-text)',
                    signed: 'var(--accent)',
                    completed: 'var(--accent)',
                    lost: 'var(--danger-text)',
                    default: 'rgba(100,116,139,0.30)',
                  };
                  const barColor = colorMap[status] || colorMap.default;
                  return (
                    <div key={status} className="flex items-center gap-3">
                      <span className="text-[11px] w-28 shrink-0 capitalize" style={{ color: 'var(--text-tertiary)' }}>{status.replace(/_/g, ' ')}</span>
                      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: barColor }} />
                      </div>
                      <span className="text-[11px] font-medium w-6 text-right" style={{ color: 'var(--text-secondary)' }}>{count}</span>
                    </div>
                  );
                })}
                {Object.keys(funnelByStatus).length === 0 && (
                  <p className="text-[13px] italic" style={{ color: 'var(--text-muted)' }}>No leads this month</p>
                )}
              </div>
            </div>

            {/* Routing status */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { key: 'assigned', label: 'Assigned', color: 'var(--accent-dim)', text: 'var(--accent)' },
                { key: 'unmatched', label: 'Unmatched', color: 'var(--danger-bg)', text: 'var(--danger-dot)' },
                { key: 'duplicate', label: 'Duplicate', color: 'var(--warning-bg)', text: 'var(--warning-dot)' },
                { key: 'invalid', label: 'Invalid', color: 'rgba(100,116,139,0.10)', text: 'var(--text-muted)' },
                { key: 'pending', label: 'Pending', color: 'rgba(59,130,246,0.12)', text: '#3b82f6' },
              ].map(r => (
                <div key={r.key} className="rounded-lg p-3 border text-center" style={{ background: r.color, borderColor: 'var(--border-separator)' }}>
                  <p className="text-lg font-semibold" style={{ color: r.text }}>{routingByStatus[r.key] || 0}</p>
                  <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{r.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Intake Schema */}
      <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
        <div className="px-5" style={{ borderBottom: expandedSections.has('intake-schema') ? '1px solid var(--border-separator)' : 'none' }}>
          <SectionHeader label="Intake Schema" icon={<FileText size={15} />} sectionKey="intake-schema" />
        </div>
        {expandedSections.has('intake-schema') && (
          <div className="px-5 py-4">
            {editing ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Duplicate Window (days)</label>
                    <input
                      type="number"
                      value={editForm.duplicate_window_days}
                      onChange={e => setEditForm(prev => ({ ...prev, duplicate_window_days: parseInt(e.target.value) || 0 }))}
                      className="w-full text-sm px-3 py-2 rounded-lg border focus:outline-none focus:ring-1"
                      style={{ borderColor: 'var(--border-primary)', background: 'var(--bg-input)', color: 'var(--text-primary)', focusRingColor: 'var(--accent-dim)' }}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editForm.requires_tcpa_consent}
                      onChange={e => setEditForm(prev => ({ ...prev, requires_tcpa_consent: e.target.checked }))}
                      className="w-4 h-4 rounded"
                    />
                    <label className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>Requires TCPA Consent</label>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={saveEdit}
                    className="px-4 py-2 text-[12px] font-medium rounded-lg text-white transition-colors"
                    style={{ background: 'var(--accent)' }}
                  >
                    <Save size={13} className="inline mr-1" /> Save Changes
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className="px-4 py-2 text-[12px] font-medium rounded-lg border transition-colors"
                    style={{ borderColor: 'var(--border-primary)', color: 'var(--text-tertiary)' }}
                  >
                    <X size={13} className="inline mr-1" /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Duplicate Window', value: `${vertical.duplicate_window_days} days` },
                    { label: 'TCPA Required', value: vertical.requires_tcpa_consent ? 'Yes' : 'No' },
                    { label: 'Lifecycle Stages', value: `${vertical.lifecycle_statuses.length} stages` },
                    { label: 'Schema Fields', value: `${Object.keys(vertical.intake_schema.fields || {}).length} fields` },
                  ].map(item => (
                    <div key={item.label} className="rounded-lg p-3 border" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-separator)' }}>
                      <p className="text-[11px] font-medium" style={{ color: 'var(--text-muted)' }}>{item.label}</p>
                      <p className="text-sm font-medium mt-0.5" style={{ color: 'var(--text-secondary)' }}>{item.value}</p>
                    </div>
                  ))}
                </div>
                <pre className="rounded-xl p-4 text-xs overflow-x-auto font-mono border"
                  style={{ background: 'var(--bg-input)', color: 'var(--text-muted)', borderColor: 'var(--border-primary)' }}
                >
                  {JSON.stringify(vertical.intake_schema, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Properties */}
      <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
        <div className="px-5" style={{ borderBottom: expandedSections.has('properties') ? '1px solid var(--border-separator)' : 'none' }}>
          <SectionHeader label="Properties" icon={<MapPin size={15} />} sectionKey="properties" count={properties.length} />
        </div>
        {expandedSections.has('properties') && (
          <div className="divide-y" style={{ borderColor: 'var(--border-separator)' }}>
            {properties.map(p => (
              <div key={p.id} className="flex items-center justify-between px-5 py-3.5 transition-colors hover-surface"
              >
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: 'var(--bg-input)' }}>
                    <Globe size={13} style={{ color: 'var(--text-muted)' }} />
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{p.name}</p>
                    <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{p.domain} · {p.active ? 'Active' : 'Inactive'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    {mockLeads.filter(l => l.property_id === p.id && !l.deleted_at).length} leads
                  </span>
                  <a
                    href={`https://${p.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-md transition-colors hover-surface hover-muted"
                  >
                    <Link2 size={13} />
                  </a>
                </div>
              </div>
            ))}
            {properties.length === 0 && (
              <p className="px-5 py-4 text-[13px] italic" style={{ color: 'var(--text-muted)' }}>No properties configured</p>
            )}
          </div>
        )}
      </div>

      {/* Offers */}
      <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
        <div className="px-5" style={{ borderBottom: expandedSections.has('offers') ? '1px solid var(--border-separator)' : 'none' }}>
          <SectionHeader label="Active Offers" icon={<FileText size={15} />} sectionKey="offers" count={activeOffers.length} />
        </div>
        {expandedSections.has('offers') && (
          <div className="divide-y" style={{ borderColor: 'var(--border-separator)' }}>
            {activeOffers.map(o => {
              const partner = mockPartners.find(p => p.id === o.partner_id);
              const offerLeads = mockLeads.filter(l => l.assigned_offer_id === o.id && !l.deleted_at);
              return (
                <div key={o.id} className="flex items-center justify-between px-5 py-3.5 transition-colors cursor-pointer hover-surface"
                  onClick={() => navigate(`/offers/${o.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: 'var(--bg-input)' }}>
                      <PhoneCall size={13} style={{ color: 'var(--text-muted)' }} />
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                        {partner?.display_name || o.partner_id}
                      </p>
                      <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                        {o.payout_model.toUpperCase()} · {o.payout_config.currency} {o.payout_config.rate_local || o.payout_config.rate_eur || o.payout_config.monthly_fee_eur || o.payout_config.pct_of_value || '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                      {offerLeads.length} leads routed
                    </span>
                    <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
                  </div>
                </div>
              );
            })}
            {activeOffers.length === 0 && (
              <p className="px-5 py-4 text-[13px] italic" style={{ color: 'var(--text-muted)' }}>No active offers</p>
            )}
          </div>
        )}
      </div>

      {/* Partners */}
      <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
        <div className="px-5" style={{ borderBottom: expandedSections.has('partners') ? '1px solid var(--border-separator)' : 'none' }}>
          <SectionHeader label="Partner Network" icon={<Users size={15} />} sectionKey="partners" count={partners.length} />
        </div>
        {expandedSections.has('partners') && (
          <div className="divide-y" style={{ borderColor: 'var(--border-separator)' }}>
            {partners.map(p => {
              const partnerOffers = mockOffers.filter(o => o.partner_id === p.id && o.vertical_id === vertical.id);
              const partnerLeads = mockLeads.filter(l => l.assigned_partner_id === p.id && l.vertical_id === vertical.id && !l.deleted_at);
              const conversion = partnerLeads.length > 0
                ? Math.round((partnerLeads.filter(l => l.status === 'signed' || l.status === 'completed').length / partnerLeads.length) * 100)
                : 0;
              return (
                <div key={p.id} className="flex items-center justify-between px-5 py-3.5 transition-colors cursor-pointer hover-surface"
                  onClick={() => navigate(`/partners/${p.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 text-[11px] font-semibold"
                      style={{ background: 'var(--bg-input)', color: 'var(--text-muted)' }}
                    >
                      {(p.display_name || '?')[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{p.display_name}</p>
                      <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                        {partnerOffers.length} offer{partnerOffers.length !== 1 ? 's' : ''} · {p.partner_tier} tier
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{partnerLeads.length} leads · {conversion}% conv.</p>
                    </div>
                    <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
                  </div>
                </div>
              );
            })}
            {partners.length === 0 && (
              <p className="px-5 py-4 text-[13px] italic" style={{ color: 'var(--text-muted)' }}>No partners configured</p>
            )}
          </div>
        )}
      </div>

      {/* Top Geos */}
      <div className="rounded-xl border overflow-hidden" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
        <div className="px-5" style={{ borderBottom: expandedSections.has('geos') ? '1px solid var(--border-separator)' : 'none' }}>
          <SectionHeader label="Top Regions" icon={<Globe size={15} />} sectionKey="geos" count={topGeos.length} />
        </div>
        {expandedSections.has('geos') && (
          <div className="px-5 py-4 space-y-2">
            {topGeos.map(g => {
              const max = topGeos[0]?.count || 1;
              const pct = (g.count / max) * 100;
              return (
                <div key={g.id} className="flex items-center gap-3">
                  <span className="text-[12px] w-24 shrink-0 truncate" style={{ color: 'var(--text-secondary)' }}>{g.name}</span>
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: 'var(--accent)' }} />
                  </div>
                  <span className="text-[11px] font-medium w-8 text-right" style={{ color: 'var(--text-secondary)' }}>{g.count}</span>
                </div>
              );
            })}
            {topGeos.length === 0 && (
              <p className="text-[13px] italic" style={{ color: 'var(--text-muted)' }}>No geographic data yet</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
